import { Divider, Money, Text } from '@belong/ui';
import NameValueTable from 'components/NameValueTable/NameValueTable';
import PaymentMethodContainer from 'containers/PaymentMethodContainer/PaymentMethodContainer';
import { StepFormLayout } from 'lease-signing-flow-v2/components/step-form-layout/step-form-layout';
import { LEASE_SIGNING_FLOW_STRINGS } from 'lease-signing-flow-v2/constants/strings';
import { PaymentPreference, PaymentPriority } from 'models/enums';
import { useState, useMemo, useEffect } from 'react';
import { WireTransferDetails } from 'lease-signing-flow-v2/components/wire-transfer-details/wire-transfer-details';
import { ZelleDetails } from 'lease-signing-flow-v2/components/zelle-details/zelle-details';
import { connect } from 'react-redux';
import { selectFullUser } from 'store/redux/user/selectors';
import clsx from 'clsx';

const initialValues = {};

type Props = {
  currentStep: any;
  onSubmit: (values?: any) => void;
  steps: any;
  fullUser?: any;
};

type PaymentStepConfig = {
  availableTabs: string[];
  showMoveInCosts: boolean;
  showUpfrontDeposit: boolean;
  description: string;
};

const getPaymentStepConfig = (
  daysUntilLeaseStart: number,
  selectedTab: string | null,
  shouldRequirePaymentMethod: boolean,
  multipleResidentLease: boolean,
  dueOnLeaseSigningAmount: number,
  isRegionEnabledForUpfrontDeposit?: boolean,
): PaymentStepConfig => {
  if (daysUntilLeaseStart <= 7) {
    return {
      availableTabs: ['today'],
      showMoveInCosts: true,
      showUpfrontDeposit: false,
      description:
        LEASE_SIGNING_FLOW_STRINGS['payment.description.within7Days'],
    };
  }

  if (daysUntilLeaseStart <= 14) {
    if (multipleResidentLease) {
      return {
        availableTabs: ['sign'],
        showMoveInCosts: true,
        showUpfrontDeposit: false,
        description:
          LEASE_SIGNING_FLOW_STRINGS[
            'payment.description.within14DaysMultiple'
          ],
      };
    }
    return {
      availableTabs: ['now'],
      showMoveInCosts: true,
      showUpfrontDeposit: false,
      description:
        LEASE_SIGNING_FLOW_STRINGS['payment.description.within14Days'],
    };
  }

  if (daysUntilLeaseStart > 14 && !shouldRequirePaymentMethod) {
    return {
      availableTabs: ['moveIn'],
      showMoveInCosts: selectedTab === 'moveIn',
      showUpfrontDeposit: false,
      description: isRegionEnabledForUpfrontDeposit
        ? LEASE_SIGNING_FLOW_STRINGS['payment.description.moreThan14Days']
        : LEASE_SIGNING_FLOW_STRINGS[
            'payment.description.moreThan14DaysNoUpfrontDeposit'
          ],
    };
  }

  if (dueOnLeaseSigningAmount === 0) {
    return {
      availableTabs: ['moveIn'],
      showMoveInCosts: selectedTab === 'moveIn',
      showUpfrontDeposit: false,
      description: isRegionEnabledForUpfrontDeposit
        ? LEASE_SIGNING_FLOW_STRINGS['payment.description.moreThan14Days']
        : LEASE_SIGNING_FLOW_STRINGS[
            'payment.description.moreThan14DaysNoUpfrontDeposit'
          ],
    };
  }

  if (multipleResidentLease) {
    return {
      availableTabs: ['sign', 'moveIn'],
      showMoveInCosts: selectedTab === 'moveIn',
      showUpfrontDeposit: selectedTab === 'sign',
      description: isRegionEnabledForUpfrontDeposit
        ? LEASE_SIGNING_FLOW_STRINGS['payment.description.moreThan14Days']
        : LEASE_SIGNING_FLOW_STRINGS[
            'payment.description.moreThan14DaysNoUpfrontDeposit'
          ],
    };
  }

  // daysUntilLeaseStart > 14
  return {
    availableTabs: ['now', 'moveIn'],
    showMoveInCosts: selectedTab === 'moveIn',
    showUpfrontDeposit: selectedTab === 'now',
    description: isRegionEnabledForUpfrontDeposit
      ? LEASE_SIGNING_FLOW_STRINGS['payment.description.moreThan14Days']
      : LEASE_SIGNING_FLOW_STRINGS[
          'payment.description.moreThan14DaysNoUpfrontDeposit'
        ],
  };
};

const PaymentStepComponent = ({
  currentStep,
  onSubmit,
  steps,
  fullUser,
}: Props) => {
  const moveInCosts = currentStep?.moveInCosts;
  const hasMoveInCosts = moveInCosts?.items && moveInCosts.items.length > 0;
  const daysUntilLeaseStart = currentStep?.daysUntilLeaseStart ?? 0;

  // Check if payment method is selected for rent
  const selectedPaymentMethod = useMemo(() => {
    return fullUser?.getPaymentMethodFor(
      PaymentPreference.Rent,
      PaymentPriority.Primary,
    );
  }, [fullUser]);

  // Determine if Next button should be disabled
  const isNextButtonDisabled = useMemo(() => {
    if (!currentStep?.shouldRequirePaymentMethod) {
      return false;
    }
    // Only check payment method when daysUntilLeaseStart > 7 (when PaymentMethodContainer is shown)
    if (daysUntilLeaseStart > 7) {
      return !selectedPaymentMethod;
    }
    return false;
  }, [
    currentStep?.shouldRequirePaymentMethod,
    daysUntilLeaseStart,
    selectedPaymentMethod,
  ]);

  const allTabOptions = [
    {
      value: 'today',
      label: LEASE_SIGNING_FLOW_STRINGS['payment.tab.dueToday'],
    },
    {
      value: 'now',
      label: LEASE_SIGNING_FLOW_STRINGS['payment.tab.dueNow'],
    },
    {
      value: 'sign',
      label: LEASE_SIGNING_FLOW_STRINGS['payment.tab.dueUponSigning'],
    },
    {
      value: 'moveIn',
      label: LEASE_SIGNING_FLOW_STRINGS['payment.tab.duePriorToMoveIn'],
    },
  ];

  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const multipleResidentLease = currentStep?.multipleResidentLease || false;

  const paymentStepConfig = useMemo(
    () =>
      getPaymentStepConfig(
        daysUntilLeaseStart,
        selectedTab,
        currentStep.shouldRequirePaymentMethod,
        multipleResidentLease,
        currentStep.moveInCosts.dueOnLeaseSigningAmount,
        currentStep.isRegionEnabledForUpfrontDeposit,
      ),
    [daysUntilLeaseStart, selectedTab],
  );

  const tabOptions = useMemo(
    () =>
      allTabOptions.filter((option) =>
        paymentStepConfig.availableTabs.includes(option.value),
      ),
    [paymentStepConfig.availableTabs],
  );

  // Initialize selected tab on mount or when config changes
  useEffect(() => {
    // Both of these reads are guarded by the `length > 0` check beside them.
    if (!selectedTab && tabOptions.length > 0) {
      setSelectedTab(tabOptions[0]!.value);
    }
  }, [tabOptions, selectedTab]);

  // Update selected tab if current selection is no longer available
  useEffect(() => {
    const isCurrentTabValid = tabOptions.some(
      (option) => option.value === selectedTab,
    );
    if (!isCurrentTabValid && tabOptions.length > 0) {
      setSelectedTab(tabOptions[0]!.value);
    }
  }, [tabOptions, selectedTab]);

  return (
    <StepFormLayout
      currentStep={currentStep}
      initialValues={initialValues}
      onSubmit={onSubmit}
      steps={steps}
      title={LEASE_SIGNING_FLOW_STRINGS['payment.title']}
      previousContent={null}
      ctaProps={{ disabled: isNextButtonDisabled }}
      getForm={() => (
        <div>
          <Text>{paymentStepConfig.description}</Text>

          <div className="my-xl">
            <>
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row gap-xs">
                  {tabOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedTab(option.value)}
                      className={`mr-lg cursor-pointer py-2xs transition-colors duration-hover ${
                        selectedTab === option.value && tabOptions.length > 1
                          ? 'border-x-0 border-b-2 border-t-0 border-solid border-navy font-semibold'
                          : 'font-normal'
                      } 
                    ${tabOptions.length === 1 ? 'font-semibold' : ''}
                    `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {tabOptions.length === 1 && (
                  <div className="font-semibold">Amount</div>
                )}
              </div>
              <div className="my-sm" />
            </>

            {/* move in costs section */}
            {hasMoveInCosts && paymentStepConfig.showMoveInCosts && (
              <>
                <NameValueTable
                  responsiveText={false}
                  noBold
                  items={moveInCosts.items
                    .filter((item: any) => {
                      if (
                        selectedTab === 'moveIn' ||
                        currentStep.moveInCosts?.dueOnLeaseSigningAmount === 0
                      ) {
                        return item.billedPriorToMoveIn === true;
                      }
                      if (
                        selectedTab === 'now' ||
                        selectedTab === 'sign' ||
                        (selectedTab === 'today' &&
                          currentStep.moveInCosts?.dueOnLeaseSigningAmount > 0)
                      ) {
                        return item.billedOnLeaseSigning === true;
                      }
                      return true;
                    })
                    .sort((a: any, b: any) => a.amount - b.amount)
                    .map((item: any) => ({
                      name: item.description,
                      value: (
                        <Money
                          value={item.amount}
                          format="DOLLARS_WITH_CENTS"
                        />
                      ),
                    }))}
                />

                <div className="my-sm border-0 border-b border-dashed border-b-gray" />
                <NameValueTable
                  responsiveText={false}
                  boldValue
                  items={[
                    {
                      name: LEASE_SIGNING_FLOW_STRINGS['payment.total'],
                      value: (
                        <Money
                          value={moveInCosts.priorToMoveInAmount}
                          format="DOLLARS_WITH_CENTS"
                        />
                      ),
                    },
                  ]}
                />
              </>
            )}
            {/* upfront deposit section */}
            {paymentStepConfig.showUpfrontDeposit && (
              <NameValueTable
                responsiveText={false}
                noBold
                items={[
                  {
                    name: LEASE_SIGNING_FLOW_STRINGS['payment.upfrontDeposit'],
                    value: (
                      <Money
                        value={
                          currentStep.moveInCosts?.dueOnLeaseSigningAmount || 0
                        }
                        format="DOLLARS_WITH_CENTS"
                      />
                    ),
                    info: 'Non-Refundable • To Be Applied Towards 1st Month’s\u00a0Rent',
                  },
                ]}
              />
            )}
          </div>

          {(!currentStep.shouldRequirePaymentMethod ||
            currentStep.daysUntilLeaseStart <= 7) && (
            <div className="mt-3xl">
              <Text
                variant="h3"
                fontWeight="semibold"
                className="mb-md text-navy"
              >
                {currentStep.daysUntilLeaseStart <= 7
                  ? LEASE_SIGNING_FLOW_STRINGS['payment.urgentMove.title']
                  : LEASE_SIGNING_FLOW_STRINGS[
                      'payment.urgentMove.description'
                    ]}
              </Text>
              {moveInCosts?.wireTransferAccount && (
                <WireTransferDetails
                  wireTransferAccount={moveInCosts.wireTransferAccount}
                />
              )}
              {moveInCosts?.zelle && (
                <div className={clsx('mx-auto my-md w-full')}>
                  <Divider className="gap-xs" direction="horizontal">
                    <Text className="text-gray">Or</Text>
                  </Divider>
                </div>
              )}
              {moveInCosts?.zelle && (
                <div
                  className={moveInCosts?.wireTransferAccount ? 'mt-lg' : ''}
                >
                  <ZelleDetails zelle={moveInCosts.zelle} />
                </div>
              )}
            </div>
          )}

          {currentStep.daysUntilLeaseStart > 7 &&
            currentStep.shouldRequirePaymentMethod && (
              <>
                <div className="mt-2xl" />
                <PaymentMethodContainer
                  paymentType={PaymentPreference.Rent}
                  isRenter
                  isReferral={false}
                  primaryOnly
                  hasLabels={false}
                />
              </>
            )}
        </div>
      )}
    />
  );
};

export const PaymentStep = connect((state) => ({
  fullUser: selectFullUser(state),
}))(PaymentStepComponent);
